import * as Icons from "lucide-react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import useSeo from "../lib/useSeo";
import { BUSINESS_SERVICES, PERSONAL_SERVICES, WHY_CHOOSE } from "../lib/content";

const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Scale;
  return <Cmp className={className} strokeWidth={1.4} />;
};

const ServiceCard = ({ item, index }) => (
  <Reveal delay={(index % 3) * 0.08}>
    <div className="group h-full rounded-2xl border border-black/5 bg-white p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-transform duration-300 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)]">
      <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-bone text-gold transition-colors duration-300 group-hover:bg-navy">
        <Icon name={item.icon} className="h-6 w-6" />
      </div>
      <h3 className="font-display text-2xl font-medium text-navy">{item.title}</h3>
      <p className="mt-3 leading-relaxed text-slate-600">{item.body}</p>
    </div>
  </Reveal>
);

const CategorySection = ({ id, label, data }) => (
  <section id={id} className="px-6 py-20 lg:px-10 lg:py-24 scroll-mt-28">
    <div className="mx-auto max-w-7xl">
      <Reveal className="mb-14 max-w-2xl">
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-gold">{label}</p>
        <h2 className="font-display text-4xl font-medium leading-tight tracking-tight text-navy lg:text-5xl">
          {data.title}
        </h2>
        <p className="mt-4 text-slate-600 lg:text-lg">{data.intro}</p>
      </Reveal>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {data.items.map((item, i) => (
          <ServiceCard key={item.title} item={item} index={i} />
        ))}
      </div>
    </div>
  </section>
);

export default function Services() {
  useSeo({
    title: "Our Services",
    description:
      "Business (B2B) and personal (B2C) legal consulting: contract drafting, CIPC & POPIA compliance, labour relations, litigation support, leases, CCMA guidance, consumer protection, wills and private mediation.",
    path: "/services",
  });

  return (
    <>
      <section className="px-6 pt-40 pb-10 lg:px-10 lg:pt-48">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="mb-6 text-xs font-bold uppercase tracking-[0.25em] text-gold">Our Services</p>
            <h1 className="max-w-4xl font-display text-5xl font-semibold leading-[0.98] tracking-tight text-navy sm:text-6xl lg:text-7xl">
              Two practices. One standard of clarity.
            </h1>
          </Reveal>
          <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-3">
            <a href="#business" data-testid="tab-business" className="rounded-full bg-navy px-6 py-3 text-sm font-medium text-white transition-colors duration-300 hover:bg-royal">
              Business Services
            </a>
            <a href="#personal" data-testid="tab-personal" className="rounded-full border border-navy/20 px-6 py-3 text-sm font-medium text-navy transition-colors duration-300 hover:bg-navy hover:text-white">
              Personal Services
            </a>
          </Reveal>
        </div>
      </section>

      <CategorySection id="business" label="Business Services · B2B" data={BUSINESS_SERVICES} />

      <div className="mx-auto max-w-7xl px-6 lg:px-10"><div className="h-px bg-black/10" /></div>

      <CategorySection id="personal" label="Personal Services · B2C" data={PERSONAL_SERVICES} />

      {/* Why choose recap */}
      <section className="bg-[#FDFBF7] px-6 py-24 lg:px-10">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-12 max-w-xl">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-gold">The IntoLaw difference</p>
            <h2 className="font-display text-4xl font-medium text-navy lg:text-5xl">Why clients choose us.</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE.map((w, i) => (
              <Reveal key={w.title} delay={(i % 3) * 0.06}>
                <div className="rounded-2xl border border-black/5 bg-white p-8">
                  <span className="font-display text-4xl font-medium text-gold">0{i + 1}</span>
                  <h3 className="mt-4 font-display text-2xl font-medium text-navy">{w.title}</h3>
                  <p className="mt-2 text-slate-600">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14">
            <Link
              to="/contact"
              data-testid="services-contact-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 font-medium text-white transition-colors duration-300 hover:bg-royal"
            >
              Discuss your matter <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
